import { CheckCircleFilled, ExclamationCircleFilled, ExportOutlined } from "@ant-design/icons";
import { AnalyticsEventNameType, WebDashboardSummary } from "@coeff/api";
import { Dropdown, Menu, Tooltip } from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import {
  parseRestoreError,
  useWebDashboards,
  useWebDashboardPlanUsage,
  useRenameWebDashboardMutation,
  useDeleteWebDashboardMutation,
  useArchiveWebDashboardMutation,
  useDuplicateWebDashboardMutation,
} from "../../../api";
import {
  ArchiveIcon,
  RestoreIcon,
  InputSearch,
  FilledSelect,
  FlexRow,
  EllipsisMenu,
  DownArrow,
  DuplicateIcon,
  LoaderWithPerfTimings,
  Modal,
  Typography,
  CenterAlign,
  message,
  LinkIcon,
  AlertMessageIcon,
  DeleteIcon,
  Copy,
  InfoCircleIcon,
  MemoryIcon,
} from "../../../components";
import { COLORS } from "../../../constants";
import { AppState } from "../../../store";
import {
  DARK_FADE_TOP_PERCENT,
  FADE_HEIGHT_FRACTION,
  getLegacyFadeStopPercent,
  THUMBNAIL_ASPECT_RATIO,
  useTrack,
} from "../../../utils";
import { submitWebDashboardProactiveSalesForm } from "../../Billing/billing.utils";
import {
  DeleteDashboardModal,
  Header,
  MemoryModal,
  PlanLimitUsageBar,
  RenameDashboardModal,
} from "../components";
import { DuplicateDashboardModal } from "../components/DuplicateDashboardModal";

const Container = styled.div`
  padding: 26px 32px;
`;

const PlanUsageSection = styled.div`
  margin-bottom: 24px;
  /* Keep the usage meter a compact readable width instead of spanning the whole page. */
  max-width: 480px;
`;

// Figma 6419:6062: search (280x32) on the left, the two selects right-aligned with a 10px gap,
// and a 20px drop to the first card row.
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  .coeff__input-search {
    margin: 0;
    max-width: 280px;
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

// Memory entry point in the Dashboards header (right-aligned, per Figma). The
// icon uses currentColor, so no Tailwind dependency in the main workspace doc.
const MemoryHeaderButton = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 6px 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.black85};
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: ${COLORS.black4};
  }
`;

// Figma "Search" 6419:6069: 280x32, #F5F5F5 fill, 4px radius, muted 14/500 placeholder.
const StyledInputSearch = styled(InputSearch)`
  && {
    width: 100%;
    max-width: 280px;
    height: 32px;
    border-radius: 4px;

    input {
      font-size: 14px;
      font-weight: 500;
    }

    input::placeholder {
      color: ${COLORS.black30};
    }

    .anticon-search svg {
      width: 16px;
      height: 16px;
    }
  }
`;

// Figma "Sort by" 6419:6063/6419:6066 hugs its label (156px and 189px respectively) instead of a
// fixed box: 32px tall, 8px left / 12px right padding, capped at the design's 220px.
const ToolbarSelect = styled(FilledSelect)`
  && {
    width: auto;
    min-width: 120px;
    max-width: 220px;

    .ant-select-selector {
      height: 32px;
      padding-left: 8px;
      /* Label, then the design's 6px gap + 16px chevron + 12px edge padding. */
      padding-right: 34px;
    }

    .ant-select-arrow {
      right: 12px;
    }
  }
` as typeof FilledSelect;

// Figma draws a 336px card with 32px gutters (6419:6061 shows three of them in a 1072px band).
// The page is full-width, so the column COUNT is what adapts: auto-fill lays down as many
// ~Figma-sized columns as fit and lets each flex from 320px up to fill the row exactly — every
// row runs edge to edge, so the grid's right edge always meets the toolbar's right-aligned
// filters instead of stopping short of them. At the mock's own 1440 frame width this resolves to
// its exact three-column composition; a fixed column count is what it replaces, since three
// stretched 500px+ tiles read as a continuous strip, not a card grid.
const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 32px;
`;

// Shared by the three hover-revealed treatments (card shadow, thumbnail overlay, kabob menu) so
// they fade in together as one motion rather than drifting apart.
const HOVER_TRANSITION = "0.2s ease";

// Per Figma 6419:6088, a resting card has no border and no shadow of its own — the thumbnail's own
// border is what separates it from the page, and the card only materialises on hover.
const DashboardCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  background-color: ${COLORS.white};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow ${HOVER_TRANSITION};

  &:hover {
    box-shadow: 0px 0px 16px 0px ${COLORS.black8}, 0px 16px 24px 0px ${COLORS.black8};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Thumbnail ratio/fade geometry lives in utils/dashboardThumbnails, shared with the viewer's
// My Dashboards popover so a screenshot classifies identically on every surface.

const CardThumbnail = styled.div<{ $fadeStopPercent?: number }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${THUMBNAIL_ASPECT_RATIO};
  background-color: ${COLORS.black4 ?? "#f5f5f5"};
  border: 1px solid ${COLORS.borderPrimary};
  border-radius: 8px;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* A legacy screenshot is a different shape from the box (they were captured at 1185px wide by
     whatever height two block rows came to). Per Figma 6419:6089 it is scaled to the box's WIDTH and
     top-aligned rather than cover-cropped, so nothing is cut off the sides — the leftover strip at
     the bottom is what the fades below cover. A source taller than the box overflows instead and is
     clipped by the box's own overflow, which the same fades soften. */
  ${p =>
    p.$fadeStopPercent !== undefined &&
    `
    img {
      height: auto;
    }
  `}

  /* Resting state, per Figma 6419:6089: the bottom of a legacy thumbnail dissolves into the card
     instead of ending on a hard edge — a white fade (::before) that reaches fully opaque exactly
     where the image ends, plus a dark gradient (::after) over the box's bottom 66px. Neither is
     drawn at rest when the source already matches the box ratio. On hover the white fade gives way
     to the scrim + Open button, but the dark gradient is part of the hover treatment too — see the
     hover rules below. */
  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: ${p => (p.$fadeStopPercent === undefined ? 0 : 1)};
    transition: opacity ${HOVER_TRANSITION};
  }

  /* Figma "Rectangle 149356" plus the solid strip below it, generalised: the fade is 66px tall and
     lands on opaque white at the image's own bottom edge, wherever the source ratio puts that. A CSS
     gradient holds its final colour past the last stop, so the strip is covered by the same layer. */
  &::before {
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) ${p => (p.$fadeStopPercent ?? 100) - FADE_HEIGHT_FRACTION * 100}%,
      ${COLORS.white} ${p => p.$fadeStopPercent ?? 100}%
    );
  }

  &::after {
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) ${DARK_FADE_TOP_PERCENT}%,
      rgba(0, 0, 0, 0.2)
    );
  }

  ${DashboardCard}:hover &::before,
  ${DashboardCard}:focus-within &::before {
    opacity: 0;
  }

  /* Figma's hover cards (6429:8271, 6432:8369) layer the 66px bottom gradient —
     linear-gradient(0deg, #000 0%, transparent 100%) at 20% — over EVERY hovered thumbnail,
     legacy or 16:9, beneath the flat scrim. */
  ${DashboardCard}:hover &::after,
  ${DashboardCard}:focus-within &::after {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      transition: none;
    }
  }
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  opacity: 0;
  /* Inert while invisible: opacity alone keeps the Open link hit-testable, and on touch (where
     hover never reveals it first) a tap on the thumbnail centre would hit the hidden link and
     open a new tab instead of the card's own same-tab navigation. */
  pointer-events: none;
  transition: opacity ${HOVER_TRANSITION};
  /* Above CardThumbnail's resting fade pseudo-elements, which are later DOM children and would
     otherwise paint over the scrim and the Open button while they fade out. */
  z-index: 1;

  ${DashboardCard}:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  /* The Open button inside is focusable, so reveal the overlay on keyboard focus too —
     otherwise Tab lands on an invisible control. */
  ${DashboardCard}:focus-within & {
    opacity: 1;
    pointer-events: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// A real anchor rather than a div, so cmd/ctrl/middle-click and "Open link in new tab" all work
// natively and the control is keyboard-reachable.
const OpenDashboardButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 32px;
  max-width: 220px;
  padding: 7px 12px;
  background-color: ${COLORS.coeblue4};
  border-radius: 4px;
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;

  /* Override the global anchor link colors (antd) in every state. */
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: ${COLORS.white};
    text-decoration: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(../../../static/dashboard-thumbnail.png);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

/**
 * Thumbnail box for one card. Owns the loaded screenshot's natural ratio, because that is what
 * decides between the two treatments: a correctly sized (16:9) screenshot fills the box exactly and
 * wants a clean edge, while a legacy one is fit to the box's width, top-aligned, and has its bottom
 * dissolved into the card.
 *
 * The fallback placeholder art is already 16:9, so it never fades. Children are the hover overlay.
 */
const DashboardCardThumbnail: React.FC<{
  screenshotUrl?: string | null;
  title?: string | null;
  children?: React.ReactNode;
}> = ({ screenshotUrl, title, children }) => {
  // Starts undefined so a correctly sized screenshot never flashes a gradient it doesn't need; a
  // legacy one eases its fades in on load via the shared opacity transition.
  const [fadeStopPercent, setFadeStopPercent] = useState<number | undefined>(undefined);

  useEffect(() => {
    setFadeStopPercent(undefined);
  }, [screenshotUrl]);

  return (
    <CardThumbnail $fadeStopPercent={fadeStopPercent}>
      {screenshotUrl ? (
        <img
          src={screenshotUrl}
          alt={title || "Dashboard"}
          onLoad={e => {
            const img = e.currentTarget;
            setFadeStopPercent(getLegacyFadeStopPercent(img.naturalWidth, img.naturalHeight));
          }}
        />
      ) : (
        <ThumbnailPlaceholder />
      )}
      {children}
    </CardThumbnail>
  );
};

const CardInfo = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  /* The card's own 8px padding supplies the horizontal inset. */
  padding: 12px 0;
`;

const CardMeta = styled.div`
  flex: 1;
  /* Reserves room for the kabob and keeps the title/subtitle ellipsis working. */
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardTitle = styled(Typography)`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: ${COLORS.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSubtitle = styled(Typography)`
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: ${COLORS.black50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Third line ("Viewed …" / "Created …") — smaller and lighter than the owner line.
const CardActivity = styled(Typography)`
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  color: ${COLORS.black30};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArchivedPill = styled.span`
  display: inline-block;
  margin-top: 4px;
  padding: 1px 8px;
  border-radius: 4px;
  background-color: ${COLORS.black15 ?? "#d9d9d9"};
  color: ${COLORS.black};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ShowAllButton = styled.button`
  display: block;
  margin: 24px auto 0;
  padding: 8px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: none;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.coeblue4};
  cursor: pointer;

  &:hover {
    background-color: ${COLORS.black4 ?? "#f5f5f5"};
  }
`;

const MoreButton = styled.button<{ $forceVisible?: boolean }>`
  background-color: ${COLORS.white};
  border: none;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.iconSecondary};
  flex-shrink: 0;
  opacity: 0;
  transition: opacity ${HOVER_TRANSITION};

  ${DashboardCard}:hover & {
    opacity: 1;
  }

  /* Keep the trigger visible while its menu is open (the pointer may leave the card) and
     whenever it's keyboard-focused — otherwise a hover-only control is unreachable. */
  ${DashboardCard}:focus-within & {
    opacity: 1;
  }

  &:focus-visible {
    opacity: 1;
  }

  ${p => p.$forceVisible && "opacity: 1;"}

  &:hover {
    background-color: ${COLORS.black4 ?? "#f5f5f5"};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ActionMenu = styled(Menu)`
  min-width: 160px;
  padding: 4px 0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

  .ant-dropdown-menu-item {
    padding: 8px 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    line-height: 1.4;
    color: ${COLORS.black};
  }

  .ant-dropdown-menu-item-disabled:hover {
    background-color: ${COLORS.black4 ?? "#f5f5f5"};
  }
`;

const MenuItemIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${COLORS.black};
`;

const MenuItemLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.black};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 120px;
  color: ${COLORS.black45 ?? "#8c8c8c"};
  text-align: center;
`;

const ModalFooter = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 12px;
`;

// Icon + title row (icon signals info/warning/error before the heading).
const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalBodyText = styled(Typography)`
  && {
    color: ${COLORS.black65};
  }
`;

const ModalPrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 7px 12px;
  border-radius: 4px;
  border: none;
  background-color: ${COLORS.coeblue4};
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalSecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 7px 12px;
  border-radius: 4px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: ${COLORS.coeblue4};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

const ModalTitle = styled.div`
  font-weight: bold;
  font-size: 15px;
`;

// Matches the Figma modal: 480px, 20px padding, 16px gap single column, soft "shadow/small",
// 8px radius. Title renders inside the body (no separate AntD header).
const DashboardModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    box-shadow: 0px 0px 16px #ebebeb, 0px 16px 24px #ebebeb;
  }

  .ant-modal-body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    padding: 20px;
  }
`;

enum FilterValue {
  OWNED_BY_ANYONE = "owned_by_anyone",
  OWNED_BY_ME = "owned_by_me",
  NOT_OWNED_BY_ME = "not_owned_by_me",
}

// Archived dashboards are not hidden or split into a separate tab; they stay in the grid,
// marked with an ARCHIVED pill and sorted to the bottom. The grid renders this many cards
// before collapsing the rest behind a "Show all N dashboards" expander.
const INITIAL_VISIBLE_COUNT = 15;

enum SortValue {
  RECENTLY_VIEWED = "recently_viewed",
  ALPHABETICAL = "alphabetical",
  CREATED_DATE = "created_date",
  LAST_EDITED = "last_edited",
}

const filterOptions = [
  { value: FilterValue.OWNED_BY_ANYONE, label: "Owned by anyone" },
  { value: FilterValue.OWNED_BY_ME, label: "Owned by me" },
  { value: FilterValue.NOT_OWNED_BY_ME, label: "Not owned by me" },
];

const sortOptions = [
  { value: SortValue.RECENTLY_VIEWED, label: "Sort by recently viewed" },
  { value: SortValue.ALPHABETICAL, label: "Alphabetical" },
  { value: SortValue.CREATED_DATE, label: "Created date" },
  { value: SortValue.LAST_EDITED, label: "Last edited" },
];

const SEARCH_PARAM = "q";
const FILTER_PARAM = "filter";
const SORT_PARAM = "sort";

const parseFilterValue = (value: string | null): FilterValue =>
  Object.values(FilterValue).includes(value as FilterValue)
    ? (value as FilterValue)
    : FilterValue.OWNED_BY_ANYONE;

const parseSortValue = (value: string | null): SortValue =>
  Object.values(SortValue).includes(value as SortValue)
    ? (value as SortValue)
    : SortValue.RECENTLY_VIEWED;

export const WebDashboards: React.FC = () => {
  const track = useTrack();
  const history = useHistory();
  const location = useLocation();

  const initialParams = new URLSearchParams(location.search);

  const [search, setSearch] = useState(initialParams.get(SEARCH_PARAM) ?? "");
  const [filter, setFilter] = useState<FilterValue>(
    parseFilterValue(initialParams.get(FILTER_PARAM))
  );
  const [sortKey, setSortKey] = useState<SortValue>(parseSortValue(initialParams.get(SORT_PARAM)));
  const [showAll, setShowAll] = useState(false);
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  // The card's kabob trigger is hover-revealed, so track which card's menu is open to keep that
  // trigger visible once the pointer moves off the card and onto the menu.
  const [openMenuDashboardId, setOpenMenuDashboardId] = useState<string | null>(null);

  // Sync controls from the URL when it changes externally (e.g. browser back/forward),
  // so the visible filters and list stay in sync with the address bar.
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const urlSearch = params.get(SEARCH_PARAM) ?? "";
    const urlFilter = parseFilterValue(params.get(FILTER_PARAM));
    const urlSortKey = parseSortValue(params.get(SORT_PARAM));

    setSearch(current => (current === urlSearch ? current : urlSearch));
    setFilter(current => (current === urlFilter ? current : urlFilter));
    setSortKey(current => (current === urlSortKey ? current : urlSortKey));
  }, [location.search]);

  // Persist filters to the URL so they survive refresh and back/forward navigation.
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (search) {
      params.set(SEARCH_PARAM, search);
    } else {
      params.delete(SEARCH_PARAM);
    }

    if (filter !== FilterValue.OWNED_BY_ANYONE) {
      params.set(FILTER_PARAM, filter);
    } else {
      params.delete(FILTER_PARAM);
    }

    if (sortKey !== SortValue.RECENTLY_VIEWED) {
      params.set(SORT_PARAM, sortKey);
    } else {
      params.delete(SORT_PARAM);
    }

    const nextSearch = params.toString();

    if (nextSearch !== new URLSearchParams(location.search).toString()) {
      history.replace({ pathname: location.pathname, search: nextSearch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filter, sortKey]);

  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [renameDashboard, setRenameDashboard] = useState<WebDashboardSummary | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [duplicateDashboard, setDuplicateDashboard] = useState<WebDashboardSummary | null>(null);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteDashboard, setDeleteDashboard] = useState<WebDashboardSummary | null>(null);

  // Restore-limit states: warning-confirmation on the last restore, locked once exhausted.
  const [restoreWarningVisible, setRestoreWarningVisible] = useState(false);
  const [restoreLockedVisible, setRestoreLockedVisible] = useState(false);
  // Dashboard awaiting confirmation in the last-restore warning modal.
  const [pendingRestoreDashboard, setPendingRestoreDashboard] =
    useState<WebDashboardSummary | null>(null);

  // Plan-limit state: restoring would exceed the family's dashboard plan limit (distinct from the
  // per-dashboard restore cap above). Copy/CTA differ for plan managers vs plain domain users.
  const [restorePlanLimitVisible, setRestorePlanLimitVisible] = useState(false);
  const [salesRequestSent, setSalesRequestSent] = useState(false);
  const [salesSubmitting, setSalesSubmitting] = useState(false);
  // Plan usage captured from the 409, drives the "X of Y used on your plan" bar.
  const [planLimitUsage, setPlanLimitUsage] = useState<{ used: number; limit: number } | null>(
    null
  );
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const { data: dashboards, isLoading } = useWebDashboards();
  const { data: planUsage } = useWebDashboardPlanUsage();

  // At/over the plan limit the usage meter renders as a banner pill beside the page title
  // instead of the bar below it.
  const isAtPlanLimit = Boolean(
    planUsage?.plan_limit && (planUsage.plan_usage_count ?? 0) >= planUsage.plan_limit
  );
  const renameMutation = useRenameWebDashboardMutation();
  const deleteMutation = useDeleteWebDashboardMutation();
  const archiveMutation = useArchiveWebDashboardMutation();
  const duplicateMutation = useDuplicateWebDashboardMutation();

  useEffect(() => {
    document.title = "Dashboards - Coefficient Workspace";
    track(AnalyticsEventNameType.VibeDashListViewed);
  }, []);

  const filteredDashboards = useMemo(() => {
    if (!dashboards) {
      return [];
    }

    return dashboards
      .filter(d => {
        if (filter === FilterValue.OWNED_BY_ME) {
          return d.is_owner;
        }
        if (filter === FilterValue.NOT_OWNED_BY_ME) {
          return !d.is_owner;
        }
        return true;
      })
      .filter(d => !search || (d.title ?? "").toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        // Archived dashboards always sort to the bottom, regardless of the chosen sort.
        if (!!a.is_archived !== !!b.is_archived) {
          return a.is_archived ? 1 : -1;
        }
        switch (sortKey) {
          case SortValue.RECENTLY_VIEWED: {
            if (!a.last_viewed_dt && !b.last_viewed_dt) {
              return 0;
            }
            if (!a.last_viewed_dt) {
              return 1;
            }
            if (!b.last_viewed_dt) {
              return -1;
            }
            return new Date(b.last_viewed_dt).getTime() - new Date(a.last_viewed_dt).getTime();
          }
          case SortValue.ALPHABETICAL:
            return (a.title ?? "").localeCompare(b.title ?? "");
          case SortValue.CREATED_DATE:
            return new Date(b.created_dt).getTime() - new Date(a.created_dt).getTime();
          case SortValue.LAST_EDITED:
            return new Date(b.modified_dt).getTime() - new Date(a.modified_dt).getTime();
          default:
            return 0;
        }
      });
  }, [dashboards, filter, search, sortKey]);

  const visibleDashboards = useMemo(
    () => (showAll ? filteredDashboards : filteredDashboards.slice(0, INITIAL_VISIBLE_COUNT)),
    [filteredDashboards, showAll]
  );

  const dashboardPath = (dashboard: WebDashboardSummary) =>
    `/dashboards/${dashboard.web_dashboard_id}`;

  // Shared by the card click (same tab) and the overlay button (new tab) so both record the open.
  const trackDashboardOpened = (dashboard: WebDashboardSummary) => {
    track(AnalyticsEventNameType.VibeDashListDashboardOpened, {
      vibe_dash_id: dashboard.web_dashboard_id,
      vibe_dash_name: dashboard.title,
      ss_id: dashboard.spreadsheet_id,
    });
  };

  const handleCardClick = (dashboard: WebDashboardSummary) => {
    trackDashboardOpened(dashboard);
    history.push(dashboardPath(dashboard));
  };

  const openRenameModal = (dashboard: WebDashboardSummary) => {
    setRenameDashboard(dashboard);
    setRenameValue(dashboard.title || "");
    setRenameModalVisible(true);
  };

  const openDuplicateModal = (dashboard: WebDashboardSummary) => {
    setDuplicateDashboard(dashboard);
    setDuplicateModalVisible(true);
  };

  // Same-data copy: reuses the source's spreadsheet + tab versions and opens ready to view. On
  // success we navigate straight to the new copy (matches the viewer's Duplicate behavior).
  // On failure we let the error propagate so the modal shows the reason inline (not a top toast).
  const handleDuplicateSameData = async (title: string) => {
    if (!duplicateDashboard) {
      return;
    }
    const result = await duplicateMutation.mutateAsync({
      web_dashboard_id: duplicateDashboard.web_dashboard_id,
      title,
    });
    setDuplicateModalVisible(false);
    setDuplicateDashboard(null);
    message.success("Dashboard duplicated");
    history.push(`/dashboards/${result.web_dashboard_id}`);
  };

  const handleRename = async () => {
    if (!renameDashboard || !renameValue.trim()) {
      return;
    }

    const dashboard = renameDashboard;
    const newTitle = renameValue.trim();

    try {
      await renameMutation.mutateAsync({
        web_dashboard_id: dashboard.web_dashboard_id,
        title: newTitle,
      });
      message.success("Dashboard renamed");
    } catch {
      message.error("Failed to rename dashboard");
      return;
    }
    track(AnalyticsEventNameType.VibeDashListDashboardRenamed, {
      vibe_dash_id: dashboard.web_dashboard_id,
      vibe_dash_name: newTitle,
      ss_id: dashboard.spreadsheet_id,
    });
    setRenameModalVisible(false);
    setRenameDashboard(null);
  };

  const openDeleteModal = (dashboard: WebDashboardSummary) => {
    setDeleteDashboard(dashboard);
    setDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    if (!deleteDashboard) {
      return;
    }

    const dashboard = deleteDashboard;

    try {
      await deleteMutation.mutateAsync(dashboard.web_dashboard_id);
      message.success("Dashboard deleted");
    } catch {
      message.error("Failed to delete dashboard");
      return;
    }
    track(AnalyticsEventNameType.VibeDashListDashboardDeleted, {
      vibe_dash_id: dashboard.web_dashboard_id,
      vibe_dash_name: dashboard.title,
      ss_id: dashboard.spreadsheet_id,
    });
    setDeleteModalVisible(false);
    setDeleteDashboard(null);
  };

  const performArchiveToggle = async (dashboard: WebDashboardSummary, nextArchived: boolean) => {
    try {
      await archiveMutation.mutateAsync({
        web_dashboard_id: dashboard.web_dashboard_id,
        archived: nextArchived,
      });
      message.success(nextArchived ? "Dashboard archived" : "Dashboard restored");
    } catch (err) {
      // Restore-specific modals only apply on the restore path; archiving failures fall to a toast.
      const parsed = parseRestoreError(err);
      if (!nextArchived && parsed.kind === "plan_limit") {
        setPlanLimitUsage(parsed.planUsage);
        setSalesRequestSent(false);
        setRestorePlanLimitVisible(true);
        return;
      }
      if (!nextArchived && parsed.kind === "restore_locked") {
        setRestoreLockedVisible(true);
        return;
      }
      message.error(nextArchived ? "Failed to archive dashboard" : "Failed to restore dashboard");
    }
  };

  const handleArchiveToggle = async (dashboard: WebDashboardSummary) => {
    const nextArchived = !dashboard.is_archived;
    // Restoring: gate on remaining restores before the API call — block when exhausted, confirm on
    // the last one, otherwise restore directly.
    if (!nextArchived) {
      const remaining = dashboard.restores_remaining;
      if (remaining !== undefined && remaining <= 0) {
        setRestoreLockedVisible(true);
        return;
      }
      if (remaining === 1) {
        setPendingRestoreDashboard(dashboard);
        setRestoreWarningVisible(true);
        return;
      }
    }
    await performArchiveToggle(dashboard, nextArchived);
  };

  const isPlanManager = !!clientFlags?.is_plan_manager;
  const accountAdminEmail = clientFlags?.admin_emails?.[0];

  const handleContactSales = async () => {
    setSalesSubmitting(true);
    const ok = await submitWebDashboardProactiveSalesForm(
      "Dashboard plan limit reached (restore blocked)",
      clientFlags
    );
    setSalesSubmitting(false);
    if (ok) {
      setSalesRequestSent(true);
    } else {
      message.error("Failed to send. Please try again or contact support.");
    }
  };

  const handleShareClick = (dashboard: WebDashboardSummary) => {
    track(AnalyticsEventNameType.VibeDashListShareClicked, {
      vibe_dash_id: dashboard.web_dashboard_id,
      vibe_dash_name: dashboard.title,
      ss_id: dashboard.spreadsheet_id,
    });
    const url = `${window.location.origin}/dashboards/${dashboard.web_dashboard_id}`;
    navigator.clipboard.writeText(url).then(
      () => message.success("URL copied"),
      () => message.error("Failed to copy")
    );
  };

  const handleSourceSheetClick = (dashboard: WebDashboardSummary) => {
    track(AnalyticsEventNameType.VibeDashListSourceSheetClicked, {
      vibe_dash_id: dashboard.web_dashboard_id,
      vibe_dash_name: dashboard.title,
      ss_id: dashboard.spreadsheet_id,
    });
    const sheetUrl = `https://docs.google.com/spreadsheets/d/${dashboard.spreadsheet_id}`;
    window.open(sheetUrl, "_blank", "noopener,noreferrer");
  };

  const getActivityLabel = (dashboard: WebDashboardSummary): string => {
    if (dashboard.last_viewed_dt) {
      return `Viewed ${moment(dashboard.last_viewed_dt).fromNow()}`;
    }
    if (dashboard.created_dt) {
      return `Created ${moment(dashboard.created_dt).fromNow()}`;
    }
    return "";
  };

  if (isLoading) {
    return (
      <CenterAlign>
        <LoaderWithPerfTimings name="Dashboards" size="large" />
      </CenterAlign>
    );
  }

  return (
    <Container>
      <FlexRow alignItems="center" justifyContent="space-between" style={{ marginBottom: 26 }}>
        <FlexRow alignItems="center" style={{ gap: 16 }}>
          <Typography fontSize="28px" fontWeight={700} lineHeight="32px">
            Dashboards
          </Typography>
          {/* At/over the limit the meter renders as a compact banner pill beside the title. */}
          {isAtPlanLimit && planUsage?.plan_limit ? (
            <PlanLimitUsageBar
              used={planUsage.plan_usage_count ?? 0}
              limit={planUsage.plan_limit}
              meter={{
                gracePeriodEndsDt: planUsage.grace_period_ends_dt,
                showGraceBanner: isPlanManager,
              }}
            />
          ) : null}
        </FlexRow>
        <MemoryHeaderButton type="button" onClick={() => setShowMemoryModal(true)}>
          <MemoryIcon width="18px" height="18px" />
          Memory
        </MemoryHeaderButton>
      </FlexRow>

      {planUsage?.plan_limit && !isAtPlanLimit ? (
        <PlanUsageSection>
          <PlanLimitUsageBar
            used={planUsage.plan_usage_count ?? 0}
            limit={planUsage.plan_limit}
            meter={{
              gracePeriodEndsDt: planUsage.grace_period_ends_dt,
              showGraceBanner: isPlanManager,
            }}
          />
        </PlanUsageSection>
      ) : null}

      <Toolbar>
        <StyledInputSearch
          onChange={e => setSearch(e.target.value)}
          value={search}
          placeholder="Search"
        />

        <ToolbarActions>
          <ToolbarSelect
            suffixIcon={<DownArrow />}
            dropdownMatchSelectWidth={false}
            onChange={(val: FilterValue) => setFilter(val)}
            options={filterOptions}
            value={filter}
          />

          <ToolbarSelect
            suffixIcon={<DownArrow />}
            dropdownMatchSelectWidth={false}
            onChange={(val: SortValue) => setSortKey(val)}
            options={sortOptions}
            value={sortKey}
          />
        </ToolbarActions>
      </Toolbar>

      {filteredDashboards.length === 0 ? (
        <EmptyState>
          <Typography variant="body1" color="textSecondary">
            No dashboards found
          </Typography>
          <Typography variant="body3" color="textSecondary">
            Dashboards created from the add-on will appear here.
          </Typography>
        </EmptyState>
      ) : (
        <DashboardGrid>
          {visibleDashboards.map(dashboard => (
            <DashboardCard
              key={dashboard.web_dashboard_id}
              onClick={() => handleCardClick(dashboard)}
            >
              <DashboardCardThumbnail
                screenshotUrl={dashboard.screenshot_url}
                title={dashboard.title}
              >
                <ThumbnailOverlay>
                  <OpenDashboardButton
                    href={dashboardPath(dashboard)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => {
                      // Without this the card's onClick also fires and navigates this tab.
                      e.stopPropagation();
                      trackDashboardOpened(dashboard);
                    }}
                  >
                    Open Dashboard
                    <ExportOutlined />
                  </OpenDashboardButton>
                </ThumbnailOverlay>
              </DashboardCardThumbnail>

              <CardInfo>
                <CardMeta>
                  <CardTitle>{dashboard.title || "Untitled Dashboard"}</CardTitle>
                  <CardSubtitle>
                    Created by {dashboard.is_owner ? "you" : dashboard.owner_full_name}
                  </CardSubtitle>
                  {dashboard.is_archived ? (
                    <ArchivedPill>Archived</ArchivedPill>
                  ) : (
                    <CardActivity>{getActivityLabel(dashboard)}</CardActivity>
                  )}
                </CardMeta>

                <div onClick={e => e.stopPropagation()}>
                  <Dropdown
                    overlay={
                      <ActionMenu selectable={false}>
                        {!dashboard.is_archived && (
                          <Menu.Item key="share" onClick={() => handleShareClick(dashboard)}>
                            <FlexRow gap={1} alignItems="center" justifyContent="center">
                              <MenuItemIcon>
                                <LinkIcon />
                              </MenuItemIcon>
                              <MenuItemLabel>Share</MenuItemLabel>
                            </FlexRow>
                          </Menu.Item>
                        )}
                        <Menu.Item
                          key="source-sheet"
                          onClick={() => handleSourceSheetClick(dashboard)}
                        >
                          <FlexRow gap={1} alignItems="center" justifyContent="center">
                            <MenuItemIcon>
                              <AlertMessageIcon fill="currentColor" />
                            </MenuItemIcon>
                            <FlexRow
                              style={{ columnGap: 4 }}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <MenuItemLabel>Source sheet</MenuItemLabel>
                              <Tooltip
                                title={dashboard.spreadsheet_name ?? dashboard.spreadsheet_id}
                              >
                                <InfoCircleIcon color="#B0B0B0" />
                              </Tooltip>
                            </FlexRow>
                          </FlexRow>
                        </Menu.Item>
                        <Menu.Item
                          key="rename"
                          disabled={!dashboard.is_owner || dashboard.is_archived}
                          onClick={() => openRenameModal(dashboard)}
                        >
                          <Tooltip
                            title={
                              !dashboard.is_owner
                                ? "Only the owner can rename this dashboard"
                                : dashboard.is_archived
                                ? "Restore this dashboard to rename it"
                                : undefined
                            }
                          >
                            <FlexRow gap={1} alignItems="center" justifyContent="center">
                              <MenuItemIcon>
                                <Copy fill="currentColor" />
                              </MenuItemIcon>
                              <MenuItemLabel>Rename</MenuItemLabel>
                            </FlexRow>
                          </Tooltip>
                        </Menu.Item>
                        <Menu.Item
                          key="duplicate"
                          disabled={dashboard.is_archived}
                          onClick={() => openDuplicateModal(dashboard)}
                        >
                          <Tooltip
                            title={
                              dashboard.is_archived
                                ? "Restore this dashboard to duplicate it"
                                : undefined
                            }
                          >
                            <FlexRow gap={1} alignItems="center" justifyContent="center">
                              <MenuItemIcon>
                                <DuplicateIcon width="16" height="16" />
                              </MenuItemIcon>
                              <MenuItemLabel>Duplicate</MenuItemLabel>
                            </FlexRow>
                          </Tooltip>
                        </Menu.Item>
                        <Menu.Item
                          key="archive"
                          disabled={!dashboard.is_owner}
                          onClick={() => handleArchiveToggle(dashboard)}
                        >
                          <Tooltip
                            title={
                              !dashboard.is_owner
                                ? dashboard.is_archived
                                  ? "Only the owner can restore this dashboard"
                                  : "Only the owner can archive this dashboard"
                                : dashboard.is_archived
                                ? undefined
                                : "Archived dashboards are visible only to you. Refresh and AI chat are disabled."
                            }
                          >
                            <FlexRow gap={1} alignItems="center" justifyContent="center">
                              <MenuItemIcon>
                                {dashboard.is_archived ? (
                                  <RestoreIcon fill="currentColor" />
                                ) : (
                                  <ArchiveIcon fill="currentColor" />
                                )}
                              </MenuItemIcon>
                              <MenuItemLabel>
                                {dashboard.is_archived ? "Restore" : "Archive"}
                              </MenuItemLabel>
                            </FlexRow>
                          </Tooltip>
                        </Menu.Item>
                        {dashboard.is_archived && (
                          <Menu.Item
                            key="delete"
                            disabled={!dashboard.is_owner}
                            onClick={() => openDeleteModal(dashboard)}
                          >
                            <Tooltip
                              title={
                                !dashboard.is_owner
                                  ? "Only the owner can delete this dashboard"
                                  : undefined
                              }
                            >
                              <FlexRow gap={1} alignItems="center" justifyContent="center">
                                <MenuItemIcon>
                                  <DeleteIcon fill="currentColor" />
                                </MenuItemIcon>
                                <MenuItemLabel>Delete</MenuItemLabel>
                              </FlexRow>
                            </Tooltip>
                          </Menu.Item>
                        )}
                      </ActionMenu>
                    }
                    trigger={["click"]}
                    placement="bottomRight"
                    visible={openMenuDashboardId === dashboard.web_dashboard_id}
                    onVisibleChange={open =>
                      setOpenMenuDashboardId(open ? dashboard.web_dashboard_id : null)
                    }
                  >
                    <MoreButton $forceVisible={openMenuDashboardId === dashboard.web_dashboard_id}>
                      <EllipsisMenu fill={COLORS.iconSecondary} />
                    </MoreButton>
                  </Dropdown>
                </div>
              </CardInfo>
            </DashboardCard>
          ))}
        </DashboardGrid>
      )}
      {!showAll && filteredDashboards.length > INITIAL_VISIBLE_COUNT && (
        <ShowAllButton onClick={() => setShowAll(true)}>
          Show all {filteredDashboards.length} dashboards
        </ShowAllButton>
      )}
      <MemoryModal visible={showMemoryModal} onClose={() => setShowMemoryModal(false)} />
      <RenameDashboardModal
        visible={renameModalVisible}
        renameValue={renameValue}
        isLoading={renameMutation.isLoading}
        onRenameValueChange={setRenameValue}
        onConfirm={handleRename}
        onCancel={() => {
          setRenameModalVisible(false);
          setRenameDashboard(null);
        }}
      />
      <DuplicateDashboardModal
        visible={duplicateModalVisible}
        sourceName={duplicateDashboard?.title ?? ""}
        sourceUrl={
          duplicateDashboard
            ? `${window.location.origin}/dashboards/${duplicateDashboard.web_dashboard_id}`
            : ""
        }
        onDuplicateSameData={handleDuplicateSameData}
        onCancel={() => {
          setDuplicateModalVisible(false);
          setDuplicateDashboard(null);
        }}
      />
      <DeleteDashboardModal
        visible={deleteModalVisible}
        isLoading={deleteMutation.isLoading}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteDashboard(null);
        }}
      />
      <DashboardModal
        visible={restoreWarningVisible}
        onCancel={() => {
          setRestoreWarningVisible(false);
          setPendingRestoreDashboard(null);
        }}
        width={480}
      >
        <ModalTitleRow>
          <ExclamationCircleFilled style={{ color: COLORS.orange2, fontSize: 16 }} />
          <ModalTitle>Restore Limit Warning</ModalTitle>
        </ModalTitleRow>
        <ModalBodyText>
          A dashboard can only be restored 5 times. You can restore this dashboard one more time.
          After that, you&apos;ll need to contact Support to restore.
        </ModalBodyText>
        <ModalFooter>
          <ModalSecondaryButton
            onClick={() => {
              setRestoreWarningVisible(false);
              setPendingRestoreDashboard(null);
            }}
          >
            Cancel
          </ModalSecondaryButton>
          <ModalPrimaryButton
            onClick={() => {
              setRestoreWarningVisible(false);
              const dashboard = pendingRestoreDashboard;
              setPendingRestoreDashboard(null);
              if (dashboard) {
                void performArchiveToggle(dashboard, false);
              }
            }}
          >
            Proceed
          </ModalPrimaryButton>
        </ModalFooter>
      </DashboardModal>
      <DashboardModal
        visible={restoreLockedVisible}
        onCancel={() => setRestoreLockedVisible(false)}
        width={480}
      >
        <ModalTitleRow>
          <ExclamationCircleFilled style={{ color: COLORS.red, fontSize: 16 }} />
          <ModalTitle>Restore limit reached</ModalTitle>
        </ModalTitleRow>
        <ModalBodyText>
          This dashboard has already been restored 5 times and can&apos;t be restored again. Contact
          support if you need assistance.
        </ModalBodyText>
        <ModalFooter style={{ justifyContent: "flex-end" }}>
          <ModalPrimaryButton onClick={() => setRestoreLockedVisible(false)}>
            Got it
          </ModalPrimaryButton>
        </ModalFooter>
      </DashboardModal>
      <DashboardModal
        visible={restorePlanLimitVisible}
        onCancel={() => setRestorePlanLimitVisible(false)}
        width={480}
      >
        {salesRequestSent ? (
          <>
            <ModalTitleRow>
              <CheckCircleFilled style={{ color: COLORS.green, fontSize: 16 }} />
              <ModalTitle>Request sent</ModalTitle>
            </ModalTitleRow>
            <ModalBodyText>
              Our sales team will reach out with more information about upgrading your plan.
            </ModalBodyText>
          </>
        ) : (
          <>
            <ModalTitle>Dashboard limit reached</ModalTitle>
            {planLimitUsage && (
              <PlanLimitUsageBar used={planLimitUsage.used} limit={planLimitUsage.limit} />
            )}
            {isPlanManager ? (
              <ModalBodyText>
                You&apos;ve reached your plan&apos;s dashboard limit. You can archive a dashboard or
                upgrade your plan to create another dashboard.
              </ModalBodyText>
            ) : (
              <ModalBodyText>
                You&apos;ve reached your plan&apos;s dashboard limit. Archive a dashboard to free up
                space, or contact your admin
                {accountAdminEmail ? ` (${accountAdminEmail})` : ""} if you need additional
                dashboards.
              </ModalBodyText>
            )}
            <ModalFooter style={{ justifyContent: "flex-end" }}>
              {isPlanManager ? (
                <ModalPrimaryButton onClick={handleContactSales} disabled={salesSubmitting}>
                  {salesSubmitting ? "Sending..." : "Contact Sales"}
                </ModalPrimaryButton>
              ) : (
                <ModalPrimaryButton onClick={() => setRestorePlanLimitVisible(false)}>
                  Got it
                </ModalPrimaryButton>
              )}
            </ModalFooter>
          </>
        )}
      </DashboardModal>
    </Container>
  );
};
